import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0427 from "../lesson-27/lesson";
const linkLesson0427 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0427.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>The previous Lesson was about microphone arrays, this one is about speaker arrays. Sound transducer arrays are nowadays used in many fields:
<ul class=itemize1>
   <li class=itemize>medicine, where piezoelectric probes are an irreplaceable tool for ultrasonography or disease treatment,
   <li class=itemize>underwater acoustics, for measuring ocean depth or detect targets,
   <li class=itemize>home cinemas, where soundbars are used to produce surround sound with a limited size and fewer connections,
   <li class=itemize>live sound where line arrays are used to produce a high and even level of sound for the audience,
   <li class=itemize>in cathedrals, churches and other spaces with high ceilings, loudspeaker columns are also used to reduce echoes caused by late reflections,
   <li class=itemize>etc.
</ul>
<p>In general, source arrays are used to achieve a sufficient sound level in the direction of interest. For audio applications, directivity control, facilitated by the use of multiple transducers, can be applied to achieve different objectives such as:
<ul class=itemize1>
   <li class=itemize>radiation of sound to the listening area and not to the surrounding area for outdoor sound applications,
   <li class=itemize>radiation of sound over a very small area by a very sharp beam for applications in museums, art exhibitions or galleries (thus avoiding crosstalk between areas) or for addressing people on a particular platform (not people on other platforms) at stations,
   <li class=itemize>limitation of reflections on walls in reverberating spaces,
   <li class=itemize>radiation of sound in all directions for measuring objective room parameters,
   <li class=itemize>generation of immersive sound for instance in ambisonics or wave field synthesis applications.
</ul>
<p>This Lesson will introduce the basics of source arrays and develop their use in live sound reinforcement.
<h3 class=sectionHead>Line array</h3>
<p>
   Consider the linear array of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   loudspeakers spaced by the distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
   </math>
   shown in <a href="#" data-img-alt="Figure 1. Loudspeaker line array. Distances from the receiving point <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>P</mi></math> to the i<sup class=textsuperscript><span class=cmr-9>th</span></sup> speaker in the line array is denoted <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>i</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/28/theory/figure-1.svg>Figure 1</a> [1]. This configuration is analogous to the microphone array presented in <a href="${linkLesson0427}" target="_blank">Lesson 27</a>. Thus, by using a theoretical development similar to that used for a microphone array, we can show that the far-field directivity <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo>(</mo>
    <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <mi mathvariant=italic>Nd</mi>
    <mo>)</mo>
   </math> is given by
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>Nkδ</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kδ</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>Nkd</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>δ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>d</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>
   is the path difference between two adjacent sound sources. As with microphone arrays, it is possible to steer the array in the desired direction by applying delays to the input signals.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. Loudspeaker line array. Distances from the receiving point <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>P</mi></math> to the i<sup class=textsuperscript><span class=cmr-9>th</span></sup> speaker in the line array is denoted <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>i</mi></mrow></msub></math>." src=${config.ASSETS_URL}/04/28/theory/figure-1.svg><figcaption class=caption>Figure 1. Loudspeaker line array. Distances from the receiving point <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>P</mi></math> to the i<sup class=textsuperscript><span class=cmr-9>th</span></sup> speaker in the line array is denoted <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>i</mi></mrow></msub></math>.</figcaption></figure>
<p>
   <a href="#" data-img-alt="Figure 2. Directivity map of a line array composed of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>10</mn></math> speakers spaced <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> cm apart." data-img-src=${config.ASSETS_URL}/04/28/theory/figure-2.svg>Figure 2</a> plots the directivity of a linear array of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>10</mn>
   </math>
   sources spaced
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>20</mn>
   </math>
   cm apart as a function of frequency. In this case, no weighting is applied, all sources are fed with the same amplitude and phase.
   <figure class=figure>
<p><img alt="Figure 2. Directivity map of a line array composed of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>10</mn></math> speakers spaced <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> cm apart." src=${config.ASSETS_URL}/04/28/theory/figure-2.svg><figcaption class=caption>Figure 2. Directivity map of a line array composed of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>10</mn></math> speakers spaced <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> cm apart.</figcaption></figure>
<p>
   It can be seen that the array is omnidirectional as long as the wavelength is large compared to the length of the array, and then becomes increasingly directive. Side lobes appear when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>></mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>dN</mi>
         </mrow>
      </mfrac>
   </math>
   and aliasing for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>></mo>
      <mi>c</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>d</mi>
   </math>. To avoid aliasing problems, line array manufacturers use:
<ul class=itemize1>
   <li class=itemize>loudspeakers to generate the low and mid frequencies as long as the source separation allows,
   <li class=itemize>and for the rest of the audio band, compression drivers are coupled to waveguides [3] which are designed to achieve a more or less homogeneous output velocity field.
</ul>
<p>
   A schematic diagram is given in <a href="#" data-img-alt="Figure 3. Front view of a line array showing the low frequency drivers and the outputs of the waveguides." data-img-src=${config.ASSETS_URL}/04/28/theory/figure-3.svg>Figure 3</a>. Solutions using 3-way or 4-way systems with progressively smaller transducers and hence reducing the distance between them can also be used. 
   <figure class=figure>
<p><img alt="Figure 3. Front view of a line array showing the low frequency drivers and the outputs of the waveguides." src=${config.ASSETS_URL}/04/28/theory/figure-3.svg><figcaption class=caption>Figure 3. Front view of a line array showing the low frequency drivers and the outputs of the waveguides.</figcaption></figure>
<h3 class=sectionHead>Near and far field behaviour</h3>
<p>
   Consider a continuous line array of length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>4</mn>
   </math>
   m with a uniform volume velocity distribution. <a href="#" data-img-alt="Figure 4. Pressure level radiated by a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> m continuous line array with a volume velocity proportional to <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn><mo class=MathClass-bin>∕</mo><mi>f</mi></math>." data-img-src=${config.ASSETS_URL}/04/28/theory/figure-4.svg>Figure 4</a> shows the response of the array as a function of distance and frequency.
   <figure class=figure>
<p><img alt="Figure 4. Pressure level radiated by a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> m continuous line array with a volume velocity proportional to <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn><mo class=MathClass-bin>∕</mo><mi>f</mi></math>." src=${config.ASSETS_URL}/04/28/theory/figure-4.svg><figcaption class=caption>Figure 4. Pressure level radiated by a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> m continuous line array with a volume velocity proportional to <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn><mo class=MathClass-bin>∕</mo><mi>f</mi></math>.</figcaption></figure>
<p>It is possible to differentiate two types of behaviour [2]:
<ul class=itemize1>
   <li class=itemize>
      a near field behaviour, for
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>r</mi>
         <mo class=MathClass-rel><</mo>
         <mfrac>
            <mrow>
               <mi>f</mi>
               <msup>
                  <mrow>
                     <mi>L</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
            <mrow>
               <mn>2</mn>
               <mi>c</mi>
            </mrow>
         </mfrac>
      </math>
      for which the pressure level exhibits oscillations and decreases on average by 3 dB when the distance doubles,
   <li class=itemize>
      a far field behaviour for which the sound level decreases by 6 dB as the distance doubles.
</ul>
<p>It can be seen that the near-field transition is frequency dependent and also that for typical line array operating distances, part of the audience is in the near-field and part in the far-field. Thus far-field quantities such as directivity are not relevant when calibrating an array for a given concert hall. It is then possible to use software, that can be provided by sound system manufacturers, to define the number and geometry of arrays and tune them for a specific concert venue. For example, numerical tools such as EASE Focus or MAPP3D, among others, can be used to simulate the performance of line array arrangements  where the files corresponding to specific line array series can be loaded and simulated.
<p>As the distance increases, high frequency attenuation due to air absorption also increases. This is solved by a high frequency compensation applied to the long range zones. Also, changes in weather conditions such as temperature may affect the response of line arrays. These may be sensed and tracked using real-time automated adjustments.
<h3 class=sectionHead>Array geometry</h3>
<p>Unweighted linear arrays allow the sound energy to be radiated predominantly perpendicular to the array axis (see <a href="#" data-img-alt="Figure 2. Directivity map of a line array composed of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>10</mn></math> speakers spaced <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> cm apart." data-img-src=${config.ASSETS_URL}/04/28/theory/figure-2.svg>Figure 2</a>). However, for live applications, the source lines are often suspended above the stage and do not point towards the audience. To overcome this problem, it is possible to :
<ul class=itemize1>
   <li class=itemize>apply weights and delays to the various signals feeding the loudspeakers so as to steer the sound energy towards the audience,
   <li class=itemize>curve the array to create arched, J-shaped or variable curvature arrays [4],
   <li class=itemize>use a combination of both.
</ul>
<p>
   <a href="#" data-img-alt="Figure 5. Pressure level radiated by a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> m line array in the vertical plane for different curvature (upper graph: straight array, middle graph: low curvature, lower graph: medium curvature). Red lines gives the array positions and geometries." data-img-src=${config.ASSETS_URL}/04/28/theory/figure-5.svg>Figure 5</a> shows pressure level maps in the vertical plane for 3 array geometries. It can be seen that the curvature of the line array allows the sound energy to be better directed towards the audience, whose ears are on average at a height of around 1.7 m.
   <figure class=figure>
      <img alt="Figure 5. Pressure level radiated by a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> m line array in the vertical plane for different curvature (upper graph: straight array, middle graph: low curvature, lower graph: medium curvature). Red lines gives the array positions and geometries." src=${config.ASSETS_URL}/04/28/theory/figure-5.svg>
      <figcaption class=caption>
         Figure 5. Pressure level radiated by a
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mn>4</mn>
         </math>
         m line array in the vertical plane for different curvature (upper graph: straight array, middle graph: low curvature, lower graph: medium curvature). Red lines gives the array positions and geometries.
      </figcaption>
   </figure>
<h3 class=sectionHead>Conclusion</h3>
<p>Nowadays, loudspeaker arrays are increasingly used. They have made it possible to replace traditional stacks of loudspeakers in live concert applications with line arrays that can be tuned to each concert venue. Thus, the sound quality has been improved: audience coverage, sound level, spectral balance, etc., while facilitating their implementation (fewer speakers, less space on stage, less weight, faster installation and dismantling). Front end numerical processors also allowed a better control of the sound distribution over the audience, allowing real-time modification of the sound rendering.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-deffarges2000reseaux class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>François Deffarges. <span class=cmti-10>Réseaux acoustiques de sources ponctuelles</span>. te5156. In French. Ed. Techniques Ingénieur, 2000.
   <dt id=X0-heil1992sound class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Christian Heil and Marcel Urban. “Sound fields radiated by multiple sound sources arrays”. In: <span class=cmti-10>Audio Engineering Society Convention 92</span>. Audio Engineering Society. 1992.
   <dt id=X0-urban2003wavefront class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Marcel Urban, Christian Heil, and Paul Bauman. “Wavefront sculpture technology”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>51.10 (2003), pp. 912–932.
   <dt id=X0-ureda2004analysis class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>Mark S Ureda. “Analysis of loudspeaker line arrays”. In: <span class=cmti-10>Journal of the </span><span class=cmti-10>Audio Engineering Society </span>52.5 (2004), pp. 467–495.
</dl>`;

export default theory;
